/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Timezone Manager - Phase 9 Migration
 * Управление временными зонами и конвертация времени
 */


/**
 * Timezone Manager Class
 * Управляет временными зонами и конвертацией времени
 */
class TimezoneManager {
    constructor() {
        this.initialized = false;
        this.timezones = new Map();
        this.userTimezone = 'Europe/Moscow';
        this.defaultTimezone = 'UTC';
        this.cache = new Map();
        
        this.initializeTimezones();
        this.detectUserTimezone();
        
        // Manager initialization logging only in debug mode
    }
    
    /**
     * Инициализация распространенных временных зон
     */
    initializeTimezones() {
        // Major timezones
        this.timezones.set('UTC', {
            name: 'Coordinated Universal Time',
            offset: 0,
            abbreviation: 'UTC',
            dstOffset: 0
        });
        
        // European timezones
        this.timezones.set('Europe/Moscow', {
            name: 'Moscow Standard Time',
            offset: 180, // +3 hours
            abbreviation: 'MSK',
            dstOffset: 0
        });
        
        this.timezones.set('Europe/London', {
            name: 'Greenwich Mean Time',
            offset: 0,
            abbreviation: 'GMT',
            dstOffset: 60 // +1 hour during DST
        });
        
        this.timezones.set('Europe/Berlin', {
            name: 'Central European Time',
            offset: 60, // +1 hour
            abbreviation: 'CET',
            dstOffset: 60 // +1 hour during DST
        });
        
        // American timezones
        this.timezones.set('America/New_York', {
            name: 'Eastern Standard Time',
            offset: -300, // -5 hours
            abbreviation: 'EST',
            dstOffset: 60 // +1 hour during DST
        });
        
        this.timezones.set('America/Chicago', {
            name: 'Central Standard Time',
            offset: -360, // -6 hours
            abbreviation: 'CST',
            dstOffset: 60
        });
        
        this.timezones.set('America/Los_Angeles', {
            name: 'Pacific Standard Time',
            offset: -480, // -8 hours
            abbreviation: 'PST',
            dstOffset: 60
        });
        
        // Asian timezones
        this.timezones.set('Asia/Tokyo', {
            name: 'Japan Standard Time',
            offset: 540, // +9 hours
            abbreviation: 'JST',
            dstOffset: 0
        });
        
        this.timezones.set('Asia/Shanghai', {
            name: 'China Standard Time',
            offset: 480, // +8 hours
            abbreviation: 'CST',
            dstOffset: 0
        });
        
        this.timezones.set('Asia/Kolkata', {
            name: 'India Standard Time',
            offset: 330, // +5:30 hours
            abbreviation: 'IST',
            dstOffset: 0
        });
    }
    
    /**
     * Определение пользовательской временной зоны
     */
    detectUserTimezone() {
        try {
            if (Intl && Intl.DateTimeFormat) {
                this.userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } else {
                // Fallback: determine by offset
                const offset = new Date().getTimezoneOffset();
                this.userTimezone = this.getTimezoneByOffset(-offset);
            }
            
            // User timezone detected logging only in debug mode
            
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[TimezoneManager] Could not detect user timezone:', error);
            }
            this.userTimezone = 'Europe/Moscow'; // Default fallback
        }
    }
    
    /**
     * Получение временной зоны
     * Мигрировано из legacy getTimezone
     */
    getTimezone(timezoneName = null) {
        const tz = timezoneName || this.userTimezone;
        
        if (this.timezones.has(tz)) {
            return {
                name: tz,
                ...this.timezones.get(tz)
            };
        }
        
        // Try to get timezone info using Intl API
        try {
            if (Intl && Intl.DateTimeFormat) {
                const now = new Date();
                const formatter = new Intl.DateTimeFormat('en', {
                    timeZone: tz,
                    timeZoneName: 'short'
                });
                
                const parts = formatter.formatToParts(now);
                const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
                
                return {
                    name: tz,
                    abbreviation: timeZoneName || 'Unknown',
                    offset: this.getTimezoneOffset(tz),
                    supported: true
                };
            }
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[TimezoneManager] Error getting timezone info:', error);
            }
        }
        
        return {
            name: tz,
            abbreviation: 'Unknown',
            offset: 0,
            supported: false
        };
    }
    
    /**
     * Установка пользовательской временной зоны
     * Мигрировано из legacy setTimezone
     */
    setTimezone(timezoneName) {
        if (!timezoneName) {
            if (debugMode === '1') {
                console.warn('[TimezoneManager] Timezone name is required');
            }
            return false;
        }
        
        try {
            // Validate timezone
            const testDate = new Date();
            new Intl.DateTimeFormat('en', { timeZone: timezoneName }).format(testDate);
            
            this.userTimezone = timezoneName;
            
            // Clear cache
            this.cache.clear();
            
            if (window.HyperLog) {
                window.HyperLog.debug('[TimezoneManager] User timezone set to:', timezoneName);
            }
            
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TimezoneManager] Invalid timezone:', timezoneName, error);
            }
            return false;
        }
    }
    
    /**
     * Конвертация времени между временными зонами
     */
    convertTime(date, fromTimezone, toTimezone) {
        if (!date) return null;
        
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            return null;
        }
        
        const cacheKey = `${dateObj.getTime()}-${fromTimezone}-${toTimezone}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }
        
        try {
            let result;
            
            if (Intl && Intl.DateTimeFormat) {
                // Modern approach using Intl API
                result = this.convertWithIntl(dateObj, fromTimezone, toTimezone);
            } else {
                // Fallback approach
                result = this.convertWithOffset(dateObj, fromTimezone, toTimezone);
            }
            
            // Cache result
            if (result) {
                this.cache.set(cacheKey, result);
                
                // Limit cache size
                if (this.cache.size > 100) {
                    const firstKey = this.cache.keys().next().value;
                    this.cache.delete(firstKey);
                }
            }
            
            return result;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TimezoneManager] Error converting time:', error);
            }
            return null;
        }
    }
    
    /**
     * Конвертация с использованием Intl API
     */
    convertWithIntl(date, fromTimezone, toTimezone) {
        // Get the time in the source timezone
        const sourceFormatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: fromTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        
        // Get the time in the target timezone
        const targetFormatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: toTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        
        const targetTime = targetFormatter.format(date);
        const convertedDate = new Date(targetTime);
        
        return {
            originalDate: date,
            convertedDate: convertedDate,
            fromTimezone: fromTimezone,
            toTimezone: toTimezone,
            originalString: sourceFormatter.format(date),
            convertedString: targetTime
        };
    }
    
    /**
     * Конвертация с использованием offset
     */
    convertWithOffset(date, fromTimezone, toTimezone) {
        const fromOffset = this.getTimezoneOffset(fromTimezone);
        const toOffset = this.getTimezoneOffset(toTimezone);
        
        const offsetDiff = (toOffset - fromOffset) * 60 * 1000; // Convert to milliseconds
        const convertedDate = new Date(date.getTime() + offsetDiff);
        
        return {
            originalDate: date,
            convertedDate: convertedDate,
            fromTimezone: fromTimezone,
            toTimezone: toTimezone,
            originalString: date.toISOString(),
            convertedString: convertedDate.toISOString()
        };
    }
    
    /**
     * Получение offset временной зоны в минутах
     */
    getTimezoneOffset(timezoneName) {
        if (this.timezones.has(timezoneName)) {
            return this.timezones.get(timezoneName).offset;
        }
        
        try {
            if (Intl && Intl.DateTimeFormat) {
                const now = new Date();
                const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
                const tz = new Date(now.toLocaleString('en-US', { timeZone: timezoneName }));
                
                return (tz.getTime() - utc.getTime()) / (1000 * 60);
            }
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[TimezoneManager] Error getting timezone offset:', error);
            }
        }
        
        return 0; // UTC fallback
    }
    
    /**
     * Получение временной зоны по offset
     */
    getTimezoneByOffset(offsetMinutes) {
        for (const [name, info] of this.timezones) {
            if (info.offset === offsetMinutes) {
                return name;
            }
        }
        
        // Generate timezone name based on offset
        const hours = Math.floor(Math.abs(offsetMinutes) / 60);
        const minutes = Math.abs(offsetMinutes) % 60;
        const sign = offsetMinutes >= 0 ? '+' : '-';
        
        return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    
    /**
     * Форматирование времени в определенной временной зоне
     */
    formatInTimezone(date, timezone = null, options = {}) {
        const tz = timezone || this.userTimezone;
        
        if (!date) return '';
        
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            return '';
        }
        
        const defaultOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: tz,
            ...options
        };
        
        try {
            if (Intl && Intl.DateTimeFormat) {
                return new Intl.DateTimeFormat('ru-RU', defaultOptions).format(dateObj);
            } else {
                // Fallback
                const converted = this.convertTime(dateObj, 'UTC', tz);
                return converted ? converted.convertedDate.toLocaleString() : dateObj.toLocaleString();
            }
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TimezoneManager] Error formatting in timezone:', error);
            }
            return dateObj.toLocaleString();
        }
    }
    
    /**
     * Получение текущего времени в определенной временной зоне
     */
    getCurrentTimeInTimezone(timezone = null) {
        const tz = timezone || this.userTimezone;
        const now = new Date();
        
        return {
            utc: now,
            local: this.formatInTimezone(now, tz),
            timezone: tz,
            offset: this.getTimezoneOffset(tz),
            timestamp: now.getTime()
        };
    }
    
    /**
     * Проверка на летнее время
     */
    isDST(date, timezone = null) {
        const tz = timezone || this.userTimezone;
        
        try {
            if (Intl && Intl.DateTimeFormat) {
                const january = new Date(date.getFullYear(), 0, 1);
                const july = new Date(date.getFullYear(), 6, 1);
                
                const janOffset = this.getTimezoneOffset(tz, january);
                const julOffset = this.getTimezoneOffset(tz, july);
                const currentOffset = this.getTimezoneOffset(tz, date);
                
                return currentOffset !== Math.max(janOffset, julOffset);
            }
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[TimezoneManager] Error checking DST:', error);
            }
        }
        
        return false;
    }
    
    /**
     * Получение списка доступных временных зон
     */
    getAvailableTimezones() {
        const timezones = {};
        
        for (const [name, info] of this.timezones) {
            timezones[name] = {
                name: info.name,
                abbreviation: info.abbreviation,
                offset: info.offset,
                offsetString: this.formatOffset(info.offset)
            };
        }
        
        return timezones;
    }
    
    /**
     * Форматирование offset в читаемый вид
     */
    formatOffset(offsetMinutes) {
        const hours = Math.floor(Math.abs(offsetMinutes) / 60);
        const minutes = Math.abs(offsetMinutes) % 60;
        const sign = offsetMinutes >= 0 ? '+' : '-';
        
        return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    
    /**
     * Добавление пользовательской временной зоны
     */
    addTimezone(name, timezoneInfo) {
        this.timezones.set(name, timezoneInfo);
        if (window.HyperLog) {
            window.HyperLog.debug('[TimezoneManager] Custom timezone added:', name);
        }
    }
    
    /**
     * Очистка кеша
     */
    clearCache() {
        this.cache.clear();
        if (window.HyperLog) {
            window.HyperLog.debug('[TimezoneManager] Cache cleared');
        }
    }
    
    /**
     * Получение статистики менеджера временных зон
     */
    getTimezoneStats() {
        return {
            initialized: this.initialized,
            userTimezone: this.userTimezone,
            defaultTimezone: this.defaultTimezone,
            cacheSize: this.cache.size,
            availableTimezones: this.timezones.size,
            intlSupport: !!(Intl && Intl.DateTimeFormat)
        };
    }
}

// Создаем singleton instance
const timezoneManager = new TimezoneManager();

// Global exports для backward compatibility
window.timezoneManager = timezoneManager;
window.getTimezone = timezoneManager.getTimezone.bind(timezoneManager);
window.setTimezone = timezoneManager.setTimezone.bind(timezoneManager);

// ES Module exports
export { timezoneManager };
export const { getTimezone, setTimezone } = timezoneManager; 